/**
 * 68.14: the commander's camera, ironed out — the pure parts (no Three.js), so they are tested: the wheel's steps
 * (one step a flick, whatever the device), and the frame the stops are placed round — a centre on the ground that
 * glides (the team, the selection, or where you panned) and a facing latched on entering, carried along the surface.
 */
import type { Vec3 } from '../types';
import { add, cross, dot, normalize, scale, sub } from '../math';

/**
 * Wheel events → stop steps. A mouse notch (≈ 100 px) steps at once; a trackpad's swipe and its momentum make one
 * step, not four: after a step the wheel must go quiet (180 ms) or travel a long way (600 px) before the next.
 */
export class WheelSteps {
  private acc = 0;
  private lastEvent = -Infinity;
  private locked = false;
  push(deltaY: number, deltaMode: number, now: number): -1 | 0 | 1 {
    const px = deltaMode === 1 ? deltaY * 40 : deltaMode === 2 ? deltaY * 800 : deltaY;
    if (now - this.lastEvent > 180) { this.acc = 0; this.locked = false; }
    this.lastEvent = now;
    if (px === 0) return 0;
    if (this.acc !== 0 && Math.sign(px) !== Math.sign(this.acc)) this.acc = 0;   // turned back
    this.acc += px;
    if (Math.abs(this.acc) < (this.locked ? 600 : 50)) return 0;
    const s = this.acc > 0 ? 1 : -1;
    this.acc = 0; this.locked = true;
    return s;
  }
}

/**
 * Lane E's look review: the command stops stand at most this many planet radii from their ground, so the diorama keeps
 * ground in frame on a small world (230 people is two radii out on the skirmish's ~110-person planet: a ball in space,
 * the tilt bands on the void). The orbit stays the wheel's last stop for the whole planet.
 */
export const COMMAND_DISTANCE_CAP = 0.45;

/** 68.14: the commander's pick snaps to a rock, a tree or a ruin within this many people of the ground under the cursor. */
export const PROP_SNAP_PEOPLE = 2.5;

/**
 * 68.14: the commander's view as one ladder of stops, ordered by distance, so each wheel notch moves one stop and every
 * stop is reached one notch at a time in both directions (the owner: "some stage between diorama and global that seems
 * to always get skipped over" — the overview stood farther out than the diorama but sat first on the wheel, so zooming
 * out from the diorama jumped it). From close to far: lane E's squad stop, a closer diorama (the owner: "can we make it
 * so diorama can zoom in more closely"), the diorama, the overview (the whole team and the points in frame), the orbit.
 * 68.14 follow-up (the owner, on master: "it would be cool to be able to zoom slightly further in for diorama … zooming in
 * a little more would be nice"): a nearer diorama between the squad stop and the close one, with the diorama's tilt.
 * The diorama keeps ground in frame (at most 0.45 radii off, lane E's review); each stop stands at least `LADDER_STEP`
 * times farther than the one before it, so no two coincide on a small world or a megaplanet.
 */
/** The stops from close to far; every index on the ladder is read from here, never written as a number. */
export const LADDER_NAMES = ['squad', 'near', 'close', 'diorama', 'overview', 'orbit'] as const;
export interface LadderStop { name: typeof LADDER_NAMES[number]; distance: number; pitch: number; fov: number; tilt: number; focus: number }
export const LADDER_STEP = 1.3;
/** A stop's place on the ladder. */
export function ladderIndex(name: LadderStop['name']): number { return LADDER_NAMES.indexOf(name); }
/** Where O enters the view the first time (the overview, as before 68.14). */
export const LADDER_ENTRY = ladderIndex('overview');
export const LADDER_DIORAMA = ladderIndex('diorama');
export const LADDER_LAST = LADDER_NAMES.length - 1;

export function commandLadder(o: { person: number; radius: number; fit: number; baseFov: number }): LadderStop[] {
  const diorama = Math.min(230 * o.person, COMMAND_DISTANCE_CAP * o.radius);
  const close = diorama / 2;
  const near = close / 1.5;
  const squad = Math.min(45 * o.person, near / LADDER_STEP);
  const orbit = 2.6 * o.radius;
  const overview = Math.min(Math.max(o.fit, diorama * LADDER_STEP), orbit / LADDER_STEP);
  return [
    { name: 'squad', distance: squad, pitch: 0.72, fov: 40, tilt: 0.45, focus: 0 },
    { name: 'near', distance: near, pitch: 0.95, fov: 30, tilt: 1, focus: 0 },
    { name: 'close', distance: close, pitch: 0.95, fov: 30, tilt: 1, focus: 0 },
    { name: 'diorama', distance: diorama, pitch: 0.95, fov: 30, tilt: 1, focus: 0 },
    { name: 'overview', distance: overview, pitch: 1.107, fov: o.baseFov, tilt: 0, focus: 0 },   // 63° down, as the overview stood
    { name: 'orbit', distance: orbit, pitch: Math.PI / 2, fov: 40, tilt: 0, focus: 1 },
  ];
}

/** The ladder at a position `s` (0 … length − 1): eased between its two stops, the distance in log (as cameraLook eases). */
export function ladderAt(l: readonly LadderStop[], s: number): LadderStop {
  const x = Math.max(0, Math.min(l.length - 1, s));
  const i = Math.min(l.length - 2, Math.floor(x));
  const a = l[i], b = l[i + 1], t = x - i, k = t * t * (3 - 2 * t);
  const lerp = (p: number, q: number) => p + (q - p) * k;
  return { name: k < 0.5 ? a.name : b.name, distance: Math.exp(lerp(Math.log(a.distance), Math.log(b.distance))), pitch: lerp(a.pitch, b.pitch), fov: lerp(a.fov, b.fov), tilt: lerp(a.tilt, b.tilt), focus: lerp(a.focus, b.focus) };
}

/** The mean of unit directions (a direction), or null for none. */
export function centreOf(dirs: readonly Vec3[]): Vec3 | null {
  if (!dirs.length) return null;
  const s = dirs.reduce((a, d) => add(a, d), { x: 0, y: 0, z: 0 });
  const l = Math.sqrt(dot(s, s));
  return l > 1e-9 ? scale(s, 1 / l) : dirs[0];
}

/** Glide a direction toward another by `k` (0–1). */
export function glide(cur: Vec3, want: Vec3, k: number): Vec3 {
  return normalize(add(scale(cur, 1 - k), scale(want, k)));
}

/** A facing carried onto the ground at `c` (its part along the ground, kept), with a fallback when it points straight up. */
export function carry(f: Vec3, c: Vec3): Vec3 {
  const t = sub(f, scale(c, dot(f, c)));
  if (dot(t, t) > 1e-10) return normalize(t);
  const any = Math.abs(c.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  return normalize(sub(any, scale(c, dot(any, c))));
}

/** The screen's right on the ground at `c` facing `f` (the camera looks down at `c` with `f` up the screen). */
export function rightOf(f: Vec3, c: Vec3): Vec3 { return normalize(cross(f, c)); }

/** Pan: move the centre along the ground — `fwd` up the screen and `right` across it, in world units on a planet of `radius`. */
export function pan(c: Vec3, f: Vec3, fwd: number, right: number, radius: number): Vec3 {
  return normalize(add(c, scale(add(scale(f, fwd), scale(rightOf(f, c), right)), 1 / radius)));
}

/** Turn the facing about the centre by `angle` radians (positive: the view turns right). */
export function turn(f: Vec3, c: Vec3, angle: number): Vec3 {
  return carry(add(scale(f, Math.cos(angle)), scale(cross(f, c), Math.sin(angle))), c);   // toward rightOf
}
